import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrdersPage from './pages/OrdersPage';
import AdminDashboard from './pages/AdminDashboard';
import ContactPage from './pages/ContactPage';
import Header from './components/Header';
import Footer from './components/Footer';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance - NO credentials mode, pure Bearer token auth
export const apiClient = axios.create({
  baseURL: API,
  timeout: 25000,
  withCredentials: false
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry failed requests up to 3 times with increasing delay
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    config._retryCount = config._retryCount || 0;
    
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
    const isServerError = error.response && [500, 502, 503, 504].includes(error.response.status);
    
    if ((isNetworkError || isServerError) && config._retryCount < 3) {
      config._retryCount += 1;
      const delay = config._retryCount * 2000;
      await new Promise(r => setTimeout(r, delay));
      return apiClient(config);
    }
    return Promise.reject(error);
  }
);

// Auth context
export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      fetchCartCount();
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await apiClient.get('/cart');
      setCartCount(response.data.items.length);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email: email.trim(), password });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        fetchCartCount();
        toast.success('Welcome back!');
        return true;
      }
      toast.error('Unexpected response. Please try again.');
      return false;
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Could not connect to server. Please refresh the page and try again.');
      }
      return false;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await apiClient.post('/auth/register', { email: email.trim(), password, name: name.trim() });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        toast.success('Account created successfully!');
        return true;
      }
      toast.error('Unexpected response. Please try again.');
      return false;
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Could not connect to server. Please refresh the page and try again.');
      }
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCartCount(0);
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, cartCount, setCartCount, fetchCartCount }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/cart" element={
              <ProtectedRoute>
                <CartPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/order-success" element={
              <ProtectedRoute>
                <OrderSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
          <Footer />
          {/* WhatsApp Chat Widget */}
          <a href="https://wa.me/918549880305?text=Hi%20TrueDroplet!%20I%20want%20to%20know%20about%20custom%20water%20bottles"
            target="_blank" rel="noopener noreferrer"
            className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
            data-testid="whatsapp-chat-widget"
            title="Chat with us on WhatsApp">
            <svg viewBox="0 0 32 32" className="w-7 h-7 fill-current">
              <path d="M16.004 0h-.008C7.174 0 .004 7.176.004 16c0 3.5 1.128 6.744 3.046 9.378L1.06 31.188l6.012-1.93A15.905 15.905 0 0016.004 32C24.83 32 32 24.822 32 16S24.83 0 16.004 0zm9.334 22.594c-.39 1.1-1.932 2.014-3.18 2.28-.854.182-1.968.326-5.72-1.23-4.802-1.99-7.892-6.868-8.132-7.188-.228-.32-1.922-2.558-1.922-4.878 0-2.32 1.216-3.46 1.648-3.934.39-.428 1.03-.624 1.642-.624.198 0 .376.01.536.018.472.02.71.048 1.022.79.39.926 1.338 3.266 1.456 3.504.12.238.238.552.078.872-.148.328-.278.53-.516.812-.238.282-.462.498-.7.8-.218.264-.464.546-.196 1.038.268.484 1.192 1.966 2.558 3.186 1.758 1.568 3.238 2.054 3.7 2.282.472.228.746.19 1.022-.114.282-.31 1.206-1.404 1.528-1.886.316-.484.64-.398 1.076-.238.44.158 2.782 1.312 3.26 1.55.472.238.79.358.908.554.118.198.118 1.142-.272 2.24z"/>
            </svg>
          </a>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
